export const MEASUREMENT_ID = 'G-FVRQ2WK436';
const ANALYTICS_DISABLE_KEY = `ga-disable-${MEASUREMENT_ID}`;

function getCurrentLanguage() {
  if (typeof document !== 'undefined' && document.documentElement.lang) {
    return document.documentElement.lang;
  }

  return undefined;
}

function canTrack() {
  return (
    typeof window !== 'undefined'
    && typeof window.gtag === 'function'
    && window[ANALYTICS_DISABLE_KEY] !== true
  );
}

export function setAnalyticsTrackingEnabled(enabled) {
  if (typeof window === 'undefined') {
    return false;
  }

  window[ANALYTICS_DISABLE_KEY] = !enabled;
  return true;
}

function cleanParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => (
      value !== undefined && value !== null && value !== ''
    )),
  );
}

function buildItem(tour, options = {}) {
  const price = typeof options.price === 'number'
    ? options.price
    : typeof tour?.price === 'number'
      ? tour.price
      : undefined;

  return cleanParams({
    item_id: String(options.itemId ?? tour?.id ?? options.itemName ?? 'unknown'),
    item_name: options.itemName ?? tour?.name ?? 'Unknown item',
    item_category: options.category ?? tour?.category,
    item_variant: options.itemVariant,
    price,
    quantity: options.quantity,
    index: options.index,
  });
}

export function trackEvent(eventName, params = {}) {
  if (!canTrack()) {
    return false;
  }

  window.gtag('event', eventName, {
    send_to: MEASUREMENT_ID,
    site_language: getCurrentLanguage(),
    ...cleanParams(params),
  });

  return true;
}

export function setAnalyticsLanguage(language) {
  if (!canTrack() || !language) {
    return false;
  }

  window.gtag('set', 'user_properties', {
    site_language: language,
  });

  return true;
}

export function trackLanguageChange({ language, previousLanguage }) {
  return trackEvent('language_change', {
    language,
    previous_language: previousLanguage,
  });
}

export function trackPageView({ pageTitle, pagePath, pageLocation }) {
  return trackEvent('page_view', {
    page_title: pageTitle,
    page_path: pagePath,
    page_location: pageLocation,
  });
}

export function trackTourCardClick({ tour, listName, index }) {
  return trackEvent('select_item', {
    item_list_name: listName,
    items: [buildItem(tour, { index })],
  });
}

export function trackTourView({ tour, price, itemVariant }) {
  return trackEvent('view_item', {
    currency: 'USD',
    value: typeof price === 'number' ? price : undefined,
    items: [buildItem(tour, { price, itemVariant })],
  });
}

export function trackViewItemList({ tours, listName }) {
  if (!Array.isArray(tours) || tours.length === 0) {
    return false;
  }

  return trackEvent('view_item_list', {
    item_list_name: listName,
    items: tours.slice(0, 10).map((tour, index) => buildItem(tour, { index })),
  });
}

export function trackSearchResults({ searchTerm, resultsCount, category }) {
  return trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
    category_filter: category,
  });
}

export function trackReserveClick({
  tour,
  selectedPrice,
  guests = 1,
  selectedRentalOption,
  rentalDays,
}) {
  return trackEvent('reserve_click', {
    currency: 'USD',
    value: typeof selectedPrice === 'number' ? selectedPrice : undefined,
    guests,
    rental_days: rentalDays,
    items: [buildItem(tour, {
      price: selectedPrice,
      itemVariant: selectedRentalOption,
      quantity: guests,
    })],
  });
}

export function trackBeginCheckout({
  tour,
  selectedPrice,
  guests = 1,
  selectedRentalOption,
  rentalDays,
}) {
  return trackEvent('begin_checkout', {
    currency: 'USD',
    value: typeof selectedPrice === 'number' ? selectedPrice : undefined,
    items: [buildItem(tour, {
      price: selectedPrice,
      itemVariant: selectedRentalOption,
      quantity: guests,
    })],
    rental_days: rentalDays,
  });
}

export function trackAddPaymentInfo({
  tour,
  selectedPrice,
  guests = 1,
  selectedRentalOption,
  rentalDays,
}) {
  return trackEvent('add_payment_info', {
    currency: 'USD',
    payment_type: 'Stripe',
    value: typeof selectedPrice === 'number' ? selectedPrice : undefined,
    items: [buildItem(tour, {
      price: selectedPrice,
      itemVariant: selectedRentalOption,
      quantity: guests,
    })],
    rental_days: rentalDays,
  });
}

export function trackLead({ leadType, formName, source, value }) {
  return trackEvent('generate_lead', {
    lead_type: leadType,
    form_name: formName,
    source,
    value,
  });
}

export function trackPurchase({
  transactionId,
  tourName,
  value,
  guests = 1,
  selectedRentalOption,
}) {
  return trackEvent('purchase', {
    transaction_id: transactionId,
    currency: 'USD',
    value,
    items: [
      cleanParams({
        item_id: transactionId || tourName || 'booking',
        item_name: tourName || 'Booking',
        item_variant: selectedRentalOption,
        quantity: guests,
        price: typeof value === 'number' ? value : undefined,
      }),
    ],
  });
}
